import { Tooltip } from '@material-ui/core';
import AnnounceKit from 'announcekit-react';
import { isEmpty } from 'lodash-es';
import moment from 'moment';
import React from 'react';

import { FreeTrialStatusEnabled, IPublisher, IUINotice } from '~/src/dash/api/console-api';
import { IconTextButton } from '~/src/dash/components/buttons/IconTextButton';
import { NoticeIcon } from '~/src/dash/components/icons/misc-icons';
import { Col, Cols, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import {
  getDemoSSAIUsername,
  getEnv,
  getPREnabled,
  getResellerTitle,
  isOtteraMainAccounts,
  isPublica,
} from '~/src/dash/resellers/utils/importResellerConfig';
import { publicaColors } from '~/src/dash/utils/colors';
import { formatDateFromUTCToOffset } from '~/src/dash/utils/date';
import { checkUserInfoMasked } from '~/src/dash/utils/roles';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { DebugBanner } from '~/src/dash/views/topbar/TopBar/DebugBanner';
import { SettingsMenu } from '~/src/dash/views/topbar/TopBar/SettingsMenu';

interface IUINoticeBannerProps {
  notices: IUINotice[];
}
const UINoticeBanner = ({ notices }: IUINoticeBannerProps) => {
  return (
    <Cols
      horizontalPadding
      extraSmallVerticalPadding
      marginBottom={spacing.small}
      gap={spacing.base}
      style={{
        backgroundColor: publicaColors.ivory,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '6px',
      }}
    >
      <Col displayFlex>
        <NoticeIcon color={publicaColors.veryDarkGray} />
      </Col>
      <Col>
        {notices.map((notice) => (
          <Text key={notice.ID} bold extraSmall>
            {notice.Detail ? notice.Description + ': ' + notice.Detail : notice.Description}
          </Text>
        ))}
      </Col>
    </Cols>
  );
};

const trialNoticeMessage = (numDaysRemaining: number) => {
  let message = '';
  const reseller = getResellerTitle();
  switch (true) {
    case numDaysRemaining > 2:
      message = `You are currently in Trial Mode. You have ${numDaysRemaining} days left before your trial expires. Please reach out to your ${reseller} contact if you have any questions!`;
      break;
    case numDaysRemaining > 0:
      message = `You only have ${numDaysRemaining} ${
        numDaysRemaining > 1 ? 'days' : 'day'
      } remaining on your free trial. Please reach out to your ${reseller} contact to avoid any interruption in service.`;
      break;
    default:
      message = `Your free trial has ended. Your account is now in read-only mode. Please reach out to your ${reseller} representative to continue to optimize your OTT revenue!`;
      break;
  }
  return message;
};

interface IFreeTrialNoticeProps {
  publisher: IPublisher;
}

const FreeTrialNotice = ({ publisher }: IFreeTrialNoticeProps) => {
  const { FreeTrialActive, FreeTrialStartDate, FreeTrialEndDate, Timezone } = publisher;
  if (FreeTrialActive !== FreeTrialStatusEnabled || !FreeTrialStartDate || !FreeTrialEndDate)
    return <></>;

  const today = moment().tz(Timezone);
  const numDaysRemaining = formatDateFromUTCToOffset(FreeTrialEndDate, Timezone).diff(
    today,
    'days',
  );

  return (
    <Tooltip title={trialNoticeMessage(numDaysRemaining)}>
      <div>
        <Cols gap={spacing.small} style={{ alignItems: 'center', cursor: 'help' }}>
          <Col>
            <Text extraSmall bold disableSelect>
              Free Trial
            </Text>
          </Col>
          <Col>
            <Text extraSmall bold highlighted disableSelect>
              {`${numDaysRemaining} ${numDaysRemaining === 1 ? 'Day' : 'Days'} Left`}
            </Text>
          </Col>
        </Cols>
      </div>
    </Tooltip>
  );
};

interface IProps {
  title: string;
  enableUINotices?: boolean;
}

export const TopBar = ({ title, enableUINotices }: IProps) => {
  const { session, user } = useBaseStore();
  const isFreeTrialAccount = session.publisher.FreeTrialActive === FreeTrialStatusEnabled;
  const isAnnounceKitDisplayed =
    (isPublica() && getDemoSSAIUsername() !== user.user.Username) ||
    isOtteraMainAccounts(session.publisher.ID);

  return (
    <Rows>
      {/* STAGING BANNER */}
      {(getEnv() === 'staging' || getPREnabled()) && (
        <Row noPadding marginBottom={spacing.small}>
          <DebugBanner />
        </Row>
      )}
      {enableUINotices && !checkUserInfoMasked(user.user) && !isEmpty(user.uiNotices) && (
        <Row noPadding>
          <UINoticeBanner notices={user.uiNotices} />
        </Row>
      )}
      <Row noPadding>
        <Cols
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Col>
            <Text extraSmall muted bold>
              {title}
            </Text>
          </Col>
          <Col>
            <Cols gap={spacing.base} style={{ justifyContent: 'flex-end', alignItems: 'center' }}>
              {isFreeTrialAccount && (
                <Col paddingRight={spacing.large}>
                  <FreeTrialNotice publisher={session.publisher} />
                </Col>
              )}
              {isAnnounceKitDisplayed && (
                <Col>
                  {/* @ts-ignore */}
                  <AnnounceKit widget='https://announcekit.co/widgets/v2/dINjO'>
                    <IconTextButton
                      handleOnClick={() => null} // main onClick is handled by AnnounceKit.
                      label='Updates'
                      iconSlug='bell_small'
                    />
                  </AnnounceKit>
                </Col>
              )}
              <Col>
                <SettingsMenu />
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Row>
    </Rows>
  );
};
